/**
 * API Client
 * Handles sending events to analytics backend with batching, retry, and offline queue
 */

import { TrackingEvent } from './types';

interface QueuedRequest {
  events: TrackingEvent[];
  attempt: number;
  timestamp: number;
}

export class ApiClient {
  private apiEndpoint: string;
  private clientId: string;
  private queue: TrackingEvent[] = [];
  private batchSize: number;
  private batchInterval: number;
  private maxRetries: number = 3;
  private retryDelay: number = 1000; // Initial retry delay in ms
  private batchTimer: number | null = null;
  private offlineQueue: QueuedRequest[] = [];
  private debug: boolean;

  constructor(
    apiEndpoint: string,
    clientId: string,
    batchSize: number = 10,
    batchInterval: number = 5000,
    debug: boolean = false
  ) {
    this.apiEndpoint = apiEndpoint;
    this.clientId = clientId;
    this.batchSize = batchSize;
    this.batchInterval = batchInterval;
    this.debug = debug;

    // Start batch timer
    this.startBatchTimer();

    // Send events before page unload
    this.setupBeforeUnloadHandler();

    // Restore offline queue
    this.restoreOfflineQueue();
  }

  /**
   * Add event to queue
   */
  track(event: TrackingEvent): void {
    this.queue.push(event);

    if (this.debug) {
    }

    // If queue is full, send immediately
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Send all queued events immediately
   */
  flush(): void {
    if (this.queue.length === 0) return;

    const eventsToSend = [...this.queue];
    this.queue = [];

    if (this.debug) {
    }

    this.sendBatch(eventsToSend);
  }

  /**
   * Send batch of events to API
   */
  private async sendBatch(events: TrackingEvent[], attempt: number = 1): Promise<void> {
    try {
      const response = await fetch(`${this.apiEndpoint}/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId: this.clientId,
          events
        }),
        keepalive: true // Ensures request completes even if page is closing
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      if (this.debug) {
      }

    } catch (error) {

      // Retry with exponential backoff
      if (attempt < this.maxRetries) {
        const delay = this.retryDelay * Math.pow(2, attempt - 1);
        
        if (this.debug) {
        }

        setTimeout(() => {
          this.sendBatch(events, attempt + 1);
        }, delay);
      } else {
        // Max retries exceeded, save to offline queue
        this.saveToOfflineQueue(events, attempt);
      }
    }
  }

  /**
   * Start batch timer
   */
  private startBatchTimer(): void {
    this.batchTimer = window.setInterval(() => {
      if (this.queue.length > 0) {
        this.flush();
      }
    }, this.batchInterval);
  }

  /**
   * Stop batch timer
   */
  private stopBatchTimer(): void {
    if (this.batchTimer !== null) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }
  }

  /**
   * Setup handler to send events before page unload
   */
  private setupBeforeUnloadHandler(): void {
    window.addEventListener('beforeunload', () => {
      this.stopBatchTimer();
      this.flush();
    });

    // Also handle visibility change (mobile)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });
  }

  /**
   * Save failed batch to offline queue (localStorage)
   */
  private saveToOfflineQueue(events: TrackingEvent[], attempt: number): void {
    try {
      const queuedRequest: QueuedRequest = {
        events,
        attempt,
        timestamp: Date.now()
      };

      this.offlineQueue.push(queuedRequest);

      // Keep only last 50 failed batches
      if (this.offlineQueue.length > 50) {
        this.offlineQueue.shift();
      }

      localStorage.setItem('chatbot_offline_queue', JSON.stringify(this.offlineQueue));

      if (this.debug) {
      }
    } catch (error) {
    }
  }

  /**
   * Restore offline queue and retry sending
   */
  private restoreOfflineQueue(): void {
    try {
      // Migrate from old key name
      const oldKey = localStorage.getItem('chatbot_analytics_offline_queue');
      if (oldKey) {
        localStorage.setItem('chatbot_offline_queue', oldKey);
        localStorage.removeItem('chatbot_analytics_offline_queue');
      }

      const stored = localStorage.getItem('chatbot_offline_queue');
      if (!stored) return;

      this.offlineQueue = JSON.parse(stored);

      if (this.offlineQueue.length > 0) {

        // Try to send offline events
        this.processOfflineQueue();
      }
    } catch (error) {
    }
  }

  /**
   * Process offline queue
   */
  private async processOfflineQueue(): Promise<void> {
    const queue = [...this.offlineQueue];
    this.offlineQueue = [];

    for (const request of queue) {
      // Skip old events (older than 7 days)
      const ageInDays = (Date.now() - request.timestamp) / (1000 * 60 * 60 * 24);
      if (ageInDays > 7) {
        continue;
      }

      await this.sendBatch(request.events, request.attempt);
    }

    // Clear offline queue from localStorage
    localStorage.removeItem('chatbot_analytics_offline_queue');
  }

  /**
   * Check if API is reachable
   */
  async ping(): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiEndpoint}/ping`, {
        method: 'GET'
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Destroy client (cleanup)
   */
  destroy(): void {
    this.stopBatchTimer();
    this.flush();
  }
}
